import { Link } from 'react-router-dom'
import { buttonClassName } from '@/components/ui/buttonStyles'
import { usePageSEO } from '@/hooks/usePageSEO'
import { useTranslation } from 'react-i18next'
import LandingFooter from '@/components/landing/LandingFooter'
import PublicHeader from '@/components/layout/PublicHeader'
import { FREE_TOOLS } from '@/data/tools'
import { TOOL_ICONS } from '@/components/tools/toolUi'
import { ArrowRightIcon, SparklesIcon } from '@heroicons/react/24/outline'

const BASE_URL = 'https://www.planwise.social'

export default function ToolsIndexPage() {
    const { t } = useTranslation()

    usePageSEO('toolsIndex', {
        canonicalPath: '/tools',
        ogType: 'website',
        ogImage: `${BASE_URL}/og.jpg`,
        breadcrumbs: [{ name: 'Free Tools' }],
        jsonLd: {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: t('tools.hub.jsonLdName'),
            description: t('tools.hub.jsonLdDescription'),
            url: `${BASE_URL}/tools`,
            hasPart: FREE_TOOLS.map((tool) => ({
                '@type': 'SoftwareApplication',
                name: t(tool.nameKey),
                url: `${BASE_URL}${tool.path}`,
                applicationCategory: 'BusinessApplication',
                operatingSystem: 'Web',
                description: t(tool.descriptionKey),
                offers: {
                    '@type': 'Offer',
                    price: '0',
                    priceCurrency: 'USD',
                },
            })),
        },
    })

    return (
        <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
            <div className="flex-grow">
                <PublicHeader />

                {/* Hero */}
                <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mx-auto text-center">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 text-sm font-medium mb-6">
                            <SparklesIcon className="w-4 h-4" />
                            <span>{t('tools.hub.badge', { count: FREE_TOOLS.length })}</span>
                        </div>
                        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                            {t('tools.hub.heading')}
                        </h1>
                        <p className="text-lg text-gray-600 dark:text-gray-400">
                            {t('tools.hub.subtitle')}
                        </p>
                    </div>
                </section>

                {/* Tool grid */}
                <section className="pb-20 px-4 sm:px-6 lg:px-8">
                    <ul className="max-w-5xl mx-auto grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {FREE_TOOLS.map((tool) => {
                            const Icon = TOOL_ICONS[tool.icon]
                            return (
                                <li key={tool.path}>
                                <Link
                                    to={tool.path}
                                    className="group flex flex-col h-full p-6 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-xl hover:-translate-y-1 motion-reduce:hover:translate-y-0 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900"
                                >
                                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl mb-5 ${tool.accent}`}>
                                        <Icon className="w-6 h-6" />
                                    </div>

                                    <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                                        {t(tool.nameKey)}
                                    </h2>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 flex-grow">
                                        {t(tool.descriptionKey)}
                                    </p>

                                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                                        {tool.platform}
                                    </div>

                                    <div className="flex items-center text-sm font-medium text-primary-600 dark:text-primary-400">
                                        <span>{t('tools.hub.openTool')}</span>
                                        <ArrowRightIcon className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
                                    </div>
                                </Link>
                                </li>
                            )
                        })}
                    </ul>
                </section>

                {/* Why free / path to product */}
                <section className="pb-24 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mx-auto bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-8 sm:p-10">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                            {t('tools.hub.whyFreeHeading')}
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                            {t('tools.hub.whyFreeParagraph1')}
                        </p>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            {t('tools.hub.whyFreeParagraph2')}
                        </p>
                        <Link to="/register" className={buttonClassName({ className: 'shadow-lg shadow-primary-500/50 group' })}>
                            {t('tools.hub.tryPlanwise')}
                            <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </Link>
                    </div>
                </section>
            </div>

            <LandingFooter />
        </div>
    )
}
